import { Component, inject, output, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UserController } from '@domain';
import { RaButtonComponent, RaInputComponent, RaToastService } from '@shared/components';
import { DocumentContractController } from 'src/app/domain/controllers/document/document-contract.controller';
import { UserSignatureConfigPartial } from 'src/app/domain/entities/partials/user-signature-config.partial';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EditDataService } from '../../../../services/_edit-data.service';

@Component({
    selector: 'user-signature',
    templateUrl: 'user-signature.component.html',
    styleUrl: 'user-signature.component.scss',
    imports: [RaSquareIconComponent, RaInputComponent, RaButtonComponent, TranslatePipe, RaIconComponent],
})
export class SignatureFlowComponent {
    public close = output<void>();
    public continue = output<void>();
    public name = new FormControl('', [Validators.required, Validators.maxLength(80)]);
    public readonly signatureOptions: { label: string; fontFamily: string }[] = [
        { label: 'Mrs Saint Delafield', fontFamily: "'Mrs Saint Delafield', cursive" },
        { label: 'Cedarville Cursive', fontFamily: "'Cedarville Cursive', cursive" },
        { label: 'Architects Daughter', fontFamily: "'Architects Daughter', cursive" },
        { label: 'Sacramento', fontFamily: "'Sacramento', cursive" },
    ];
    public selectedFontFamily = this.signatureOptions[0].fontFamily;
    public signLoading = signal(false);
    public saveSignatureLoading = signal(false);
    public showConfirmation = signal(false);

    // * [Injects]
    private _utilService = inject(UtilService);
    private _activateRoute = inject(ActivatedRoute);
    private _raToastService = inject(RaToastService);
    private _userController = inject(UserController);
    private _editDataService = inject(EditDataService);
    private _documentContractController = inject(DocumentContractController);
    private _translate = inject(TranslateService);

    public selectSignature(fontFamily: string): void {
        this.selectedFontFamily = fontFamily;
    }

    public signaturePreview(): string {
        const value = this.name.value?.trim();
        return value ? value : this._translate.instant('document.edit.signaturePreviewPlaceholder');
    }

    public handleContinue(): void {
        if (!this._utilService.isControlValid(this.name)) {
            this._raToastService.warning({
                title: this._translate.instant('common.warning'),
                message: this._translate.instant('common.fieldInvalid'),
            });

            return;
        }

        this.saveSignatureLoading.set(true);

        // Configuração da assinatura
        const signatureConfig: UserSignatureConfigPartial = {
            name: this.name.value,
            fontFamily: this.selectedFontFamily,
        };

        // Salva a configuração da assinatura
        this._userController.setSignatureConfig(signatureConfig).subscribe({
            next: () => {
                this.saveSignatureLoading.set(false);
                this.showConfirmation.set(true);
            },
            error: () => {
                this.saveSignatureLoading.set(false);
            },
        });
    }

    public closeConfirmation(): void {
        if (this.signLoading()) return;
        this.showConfirmation.set(false);
    }

    public confirmSignature(): void {
        this.signLoading.set(true);

        const contractId = this._activateRoute.snapshot.params['id'];
        this._documentContractController.publishContract(contractId).subscribe({
            next: () => {
                this.showConfirmation.set(false);
                this.signLoading.set(false);

                this.continue.emit();
            },
            error: () => {
                this.signLoading.set(false);
            },
        });
    }
}
